/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {
    Flex,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import React from 'react'
import {FormattedMessage} from 'react-intl'
// import {CheckCircleIcon} from '@salesforce/retail-react-app/app/components/icons'
import {validatePassword} from '../../utils/password-utils'

/**
 * UI component for styling password requirement line
 */
const PasswordRequirement = ({children}) => {
    // TODO: Commenting only for now
    // const iconStyles = {
    //     display: 'block',
    //     ml: isValid ? '-2px' : '-1px',
    //     mr: isValid ? '9px' : '10px',
    //     boxSize: isValid ? 4 : '14px',
    //     color: isValid ? 'green.500' : 'white',
    //     border: !isValid ? '1px solid' : 'none',
    //     borderColor: 'gray.200',
    //     borderRadius: 'full'
    // }
    return (
        <Flex align="center" paddingLeft={['23px', '26px']}>
            {/* <CheckCircleIcon {...iconStyles} /> */}
            <Text fontSize="12px">{children}</Text>
        </Flex>
    )
}

PasswordRequirement.propTypes = {
    /** Should it render in valid state */
    isValid: PropTypes.bool,

    /** The requirement text */
    children: PropTypes.any
}

/**
 * Renders a list of password requirments. Each requirement line toggles to its `isValid`
 * state when the given password value meets the associated critieria.
 */
const PasswordRequirements = ({value}) => {
    const pwValidations = validatePassword(value)

    return (
        <Stack spacing={1}>
            <PasswordRequirement isValid={pwValidations.hasMinChars}>
                <FormattedMessage
                    id="password_requirements.error.eight_letter_minimum"
                    defaultMessage="Be at least 8 characters in length."
                    description="Password requirement"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasUppercase}>
                <FormattedMessage
                    id="password_requirements.error.one_uppercase_letter"
                    defaultMessage="Contain an uppercase letter."
                    description="Password requirement"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasLowercase}>
                <FormattedMessage
                    id="password_requirements.error.one_lowercase_letter"
                    defaultMessage="Contain a lowercase letter."
                    description="Password requirement"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasNumber}>
                <FormattedMessage
                    defaultMessage="Contain a digit (0-9)."
                    description="Password requirement"
                    id="password_requirements.error.one_number"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasNoSpace}>
                <FormattedMessage
                    id="password_requirements.error.no_space"
                    defaultMessage="Not Include spaces."
                    description="Password requirement"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasSpecialChar}>
                <FormattedMessage
                    id="password_requirements.error.one_special_character"
                    defaultMessage="Contain a special character."
                    description="Password requirement"
                />
            </PasswordRequirement>
            <Text
                paddingLeft={['23px', '26px']}
                fontSize="12px"
                marginTop="20px"
            >
                <FormattedMessage
                    id="registration_fields.messages.password_history"
                    defaultMessage="Note: Cannot be one of your 4 previous passwords"
                />
            </Text>
        </Stack>
    )
}

PasswordRequirements.propTypes = {
    /** The password to check against */
    value: PropTypes.string
}

export default PasswordRequirements
